import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { apiGet, apiSend, fmtDate } from '../api';
import { subscribeApiUpdates, urlConcerns } from '../utils/apiFreshness';
import { alertFailure } from '../utils/sessionEnd';
import { theme, card, input, btnPrimary, btnSecondary } from '../theme';
// v0.10.0 - master team list for this project.
import TeamPanel from '../components/TeamPanel';
// v0.39.0 - Tenant contacts picked into the project. Replaces (for the
// new flow) the older free-text TeamPanel — TeamPanel stays for now,
// the two panels run side-by-side this rev.
// v0.17.1 — Lucide migration.
import { MapPin, FileText } from 'lucide-react';
// v0.23.0 — Name-the-report modal (also used on ProjectsScreen).
import NewReportModal from '../components/NewReportModal';
// v0.38.1 — Free-tier drawings cap at the upload entry point.
import { isFreeTier, FREE_LIMITS } from '../utils/tier';
import { getCurrentUser } from '../utils/user';
import { hasPreview } from '../utils/preview';
import UpgradePrompt from '../components/UpgradePrompt';
// v0.45.25 — Shared rename-while-uploading modal (also used by PlanScreen).
import PlanRenameModal from '../components/PlanRenameModal';
import { pdfPageToPng } from '../utils/pdfRaster';

// v0.13.1 - Plan-upload helper so the "Drop a Plan" tile can ingest a plan
// directly from the project detail page without bouncing through /plan first.
// The PDF rasteriser it used to carry (a CDN-loaded copy of PlanScreen`s) is
// gone — both screens now share src/utils/pdfRaster.js on the bundled pdfjs.
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('Read failed for ' + file.name));
    r.readAsDataURL(file);
  });
}

// Project detail screen. v0.2.0 — just header info + inline rename/
// archive. v0.3.0 adds drawings & v0.4.0 adds the pin canvas inline.
// v0.4.0 — Open Plan tile routes to /projects/:id/plan (PlanScreen).
export default function ProjectDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  // v0.249.0 — floor scan is a preview: only a sign-in the server lists for it
  // sees the tile (utils/preview.js), and the App Store build never does.
  const floorScan = hasPreview(getCurrentUser(), 'floor-scan');
  // v0.22.1 — Report scope. ?reportId in URL switches the page from
  // project-home (project metadata + reports list) to report-home
  // (tiles + report-scoped members + Import Members button). Brett:
  // "Project Details stay the same … Project page can list all the
  // reports. Members/plans and pins change for each report."
  const [searchParams, setSearchParams] = useSearchParams();
  const reportId = searchParams.get('reportId');

  // v0.45.23 — Brett: "i asked for this page to be hidden."
  // The report-scoped middle view of ProjectDetail (three tiles +
  // distribution panel) is redundant — clicking a report should land
  // straight in the defect register. Catch any URL that still carries
  // ?reportId=N on this route and forward to /register?reportId=N so
  // every landing point (Done buttons, deep-links, post-create flows)
  // gets bounced automatically without having to update each call site.
  useEffect(() => {
    if (reportId) {
      navigate(
        `/projects/${id}/register?reportId=${encodeURIComponent(reportId)}`,
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  // v0.335.0 - a reports list that could not be read says so (with Try
  // again) instead of reading "No reports yet" for a location that has one.
  const [reportsErr, setReportsErr] = useState(null);
  const [reportsTick, setReportsTick] = useState(0);
  const [creatingReport, setCreatingReport] = useState(false);
  // v0.23.0 — open the name-the-report modal instead of instant-creating.
  const [namingReport, setNamingReport] = useState(false);
  // v0.45.13 — toggle showing archived reports in the list (otherwise hidden).
  //             Persisted to localStorage so the preference survives reloads.
  const [showArchived, setShowArchived] = useState(() => {
    try { return localStorage.getItem('droppin.showArchivedReports') === '1'; }
    catch { return false; }
  });
  // v0.45.13 — busy guard while patching settingsJson.archived during un/archive.
  const [archiveBusyId, setArchiveBusyId] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  // v0.37.0 — customerName added. One customer per project; every report
  // generated under this project inherits it via report header rendering.
  const [draft, setDraft] = useState({ name: '', address: '', customerName: '' });
  // v0.38.1 — UpgradePrompt state for drawings-cap hit on upload.
  const [upgradeReason, setUpgradeReason] = useState(null);
  const [saving, setSaving] = useState(false);

  // v0.13.1 - drag-and-drop "Drop a Plan" tile state. Brett: "Make the
  // Drop a plan button a drag and drop area. We are litterally saying
  // drop a plan."
  const [dropHover, setDropHover] = useState(false);
  // v0.29.0 — multi-plan queue (was single pendingPlan). Drop N PDFs/
  // images at once, walk through them one by one. Each entry:
  // { file, defaultName }. The modal acts on planQueue[0]; on
  // confirm, the entry pops off and we either show the next entry or
  // navigate to the plan page.
  const [planQueue, setPlanQueue] = useState([]);
  // v0.29.0 — Drag counter pattern. onDragEnter / onDragLeave fire as
  // the cursor crosses child elements which made the highlight flicker
  // and feel "sensitive". A counter (increment on enter, decrement on
  // leave) only flips dropHover off when the counter is back at 0,
  // i.e. the drag has actually left the drop target's bounding box.
  const dragDepthRef = useRef(0);
  const [uploadingPlan, setUploadingPlan] = useState(false);
  const [planError, setPlanError] = useState(null);
  const planFileInputRef = useRef(null);

  // v0.18.0 — Send-a-Report tile is now smart-routed (Brett: "After i go
  // create new report it takes me to the send report screen. How can
  // this be the case i havent dropped a plan, dropped a pin yet?"):
  //
  //   * 0 defects  → toast "Drop a pin first" + stay
  //   * defects, 0 saved reports → POST a default report and navigate
  //     STRAIGHT to /report?reportId=N (no intermediate Reports list)
  //   * defects, 1+ saved reports → navigate to the Reports manager
  //
  // sendBusy gates the button while the count fetch + (possible) POST
  // are in flight. sendHint is the small inline toast that surfaces
  // "drop a pin first" without nav-ing the user anywhere.
  const [sendBusy, setSendBusy] = useState(false);
  const [sendHint, setSendHint] = useState(null);
  async function handleSendReport() {
    if (sendBusy) return;
    // v0.33.1 — HOTFIX: when a report is already active (the tiles
    // only render inside the {reportId && (<>...</>)} guard so this
    // is virtually always true), jump straight to THAT report's
    // page. Previously we always queried the project-wide defect
    // list, which mixed in defects from other reports and produced
    // the "I only raised 4 but see 7" bug.
    if (reportId) {
      navigate(`/projects/${id}/report?reportId=${encodeURIComponent(reportId)}`);
      return;
    }
    setSendBusy(true);
    setSendHint(null);
    try {
      const [defectsRaw, reportsRaw] = await Promise.all([
        // v0.335.0 - no .catch(() => []): a read that failed is not "none",
        // and taking it for none here POSTed a second report.
        apiGet(`/api/defects?projectId=${encodeURIComponent(id)}`),
        apiGet(`/api/reports?projectId=${encodeURIComponent(id)}`),
      ]);
      const defectsCount = Array.isArray(defectsRaw) ? defectsRaw.length : 0;
      const reports      = Array.isArray(reportsRaw) ? reportsRaw : [];
      if (defectsCount === 0) {
        setSendHint('Drop a pin first — there are no defects on this project yet, so there\'s nothing to put in a report.');
        return;
      }
      if (reports.length === 0) {
        // No saved reports yet — create a default one inline and go
        // straight to it.
        const created = await apiSend('POST', '/api/reports', {
          projectId: Number(id),
          name: 'New report',
          description: '',
          settingsJson: { explicitDefectIds: [] },
        });
        const newId = created.id ?? created.Id;
        navigate(`/projects/${id}/report?reportId=${newId}`);
        return;
      }
      // 1+ saved reports already exist → user wants to choose. Take
      // them to the manager.
      navigate(`/projects/${id}/reports`);
    } catch (e) {
      setSendHint('Could not check report state: ' + (e.message || String(e)));
    } finally {
      setSendBusy(false);
    }
  }

  // v0.29.0 — accept N files at once. Each valid file gets a queue
  // entry; invalid ones are skipped with a soft warning. The modal
  // (driven by planQueue[0]) walks through them one by one.
  function acceptPlanFiles(filesIn) {
    setPlanError(null);
    const files = Array.from(filesIn || []);
    if (!files.length) return;
    const valid = [];
    const rejected = [];
    for (const file of files) {
      const isPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
      const isImg = /^image\//.test(file.type) || /\.(png|jpe?g|gif|webp)$/i.test(file.name);
      if (!isPdf && !isImg) {
        rejected.push(file.name);
        continue;
      }
      const defaultName = file.name.replace(/\.[^.]+$/, '').slice(0, 200);
      valid.push({ file, defaultName });
    }
    if (rejected.length) {
      setPlanError(`Skipped ${rejected.length} file${rejected.length === 1 ? '' : 's'} (PDF / image only): ${rejected.join(', ')}`);
    }
    if (valid.length) {
      // Append, so re-dropping while a queue is active keeps the
      // earlier items pending instead of clobbering them.
      setPlanQueue(prev => [...prev, ...valid]);
    }
  }

  async function uploadCurrentPlan(nickname) {
    const head = planQueue[0];
    if (!head) return;
    // v0.38.1 — Free-tier drawings-per-report cap. drawingsCount is
    //           loaded earlier and tracks the current report's count
    //           (or the project's count if no report scope).
    if (isFreeTier() && typeof drawingsCount === 'number' && drawingsCount >= FREE_LIMITS.drawingsPerReport) {
      // v0.45.27 — also clear the queue so the rename modal closes
      // and the user lands cleanly on the upgrade prompt. Previous
      // behaviour kept planQueue intact, leaving the modal stuck
      // behind the prompt — the user re-triggered the same gate on
      // every Load click.
      setPlanQueue([]);
      setUpgradeReason('drawingsPerReport');
      return;
    }
    const { file, defaultName } = head;
    setUploadingPlan(true);
    setPlanError(null);
    let lastCreatedId = null;
    try {
      const isPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
      let dataUrl;
      if (isPdf) dataUrl = await pdfPageToPng(file, { page: 1, scale: 2 });
      else       dataUrl = await fileToDataUrl(file);
      const created = await apiSend('POST', '/api/drawings', {
        projectId: Number(id),
        reportId: reportId ? Number(reportId) : undefined,
        name: (nickname || '').trim() || defaultName,
        // v0.38.0 — preserve the original upload filename so the PDF
        // plan-page can render nickname BOLD + filename small underneath.
        sourceFileName: file.name || null,
        sourceUrl: dataUrl,
        rasterUrl: dataUrl,
        sourceMime: isPdf ? 'application/pdf' : (file.type || 'image/png'),
        pageNumber: isPdf ? 1 : null,
      });
      lastCreatedId = created.id ?? created.Id ?? null;
      // Pop the head off. Remaining entries (if any) keep the modal
      // open; otherwise the modal closes and we navigate.
      const remaining = planQueue.slice(1);
      setPlanQueue(remaining);
      // Refresh the count so the tile subtext stays in sync.
      setDrawingsCount(prev => (prev == null ? 1 : prev + 1));
      if (remaining.length === 0) {
        const planUrl = `/projects/${id}/plan${lastCreatedId ? `/${lastCreatedId}` : ''}`;
        navigate(reportId ? `${planUrl}?reportId=${encodeURIComponent(reportId)}` : planUrl);
      }
    } catch (e) {
      setPlanError(e.message || String(e));
    } finally {
      setUploadingPlan(false);
    }
  }

  // v0.29.0 — Cancel the entire queue OR skip just the current one.
  function cancelPlanQueue() { if (!uploadingPlan) setPlanQueue([]); }
  function skipCurrentPlan() { if (!uploadingPlan) setPlanQueue(prev => prev.slice(1)); }

  // v0.20.2 — Track how many plans/drawings are uploaded. Drives the
  // Drop-a-Plan tile subtext: a fresh project says "Drag a PDF or
  // image here, or click to pick"; a project with plans says "N plan(s)
  // uploaded · drop another to add" so the operator knows the state
  // before clicking. Brett: "tell the user the plans are already
  // uploaded. Do we allow them to change a plan?" Answer is yes — the
  // drop-zone still accepts new plans and they're added (not
  // replacing existing ones); the PlanScreen lets you delete old
  // drawings if needed.
  const [drawingsCount, setDrawingsCount] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const p = await apiGet(`/api/projects/${id}`);
      setProject(p);
      setDraft({ name: p.Name || '', address: p.Address || '', customerName: p.CustomerName || '' });
      setError(null);
      // Drawings count — fire-and-forget, doesn't block the page.
      // v0.22.1 — Scope by reportId when active; project-scoped
      // fallback only matters for the brief moment before the user
      // picks a report.
      try {
        const url = reportId
          ? `/api/drawings?reportId=${encodeURIComponent(reportId)}`
          : `/api/drawings?projectId=${id}`;
        const list = await apiGet(url);
        setDrawingsCount(Array.isArray(list) ? list.length : 0);
      } catch (_) { setDrawingsCount(0); }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id, reportId]);

  // v0.22.1 — Load reports list for the project. Used both by the
  // project-home reports list AND the report-home active-report
  // banner (to show the report's name).
  //
  // v0.335.0 - Brett's phone said "No reports yet" on Grill'd Claremont, which
  // has a report. Two faults: (1) the service worker answers this read from
  // its cache first and refreshes behind it, and this screen never listened
  // for the refresh (API_UPDATED), so a list cached before the report existed
  // stayed on screen; (2) a read that FAILED was shown as an empty list. Now
  // the list is read again when the worker says it changed, an empty answer
  // is checked once with the network before "No reports yet" is said, and a
  // failure is named with Try again. A re-read after the first keeps what is
  // on screen until it answers (no "Loading" flash) and, if it fails, keeps
  // the list it had.
  const reportsShownRef = useRef(false);
  useEffect(() => { reportsShownRef.current = false; setReports([]); setReportsErr(null); }, [id]);
  useEffect(() => subscribeApiUpdates((url) => {
    if (urlConcerns(url, ['reports'])) setReportsTick(t => t + 1);
  }), []);
  useEffect(() => {
    let cancelled = false;
    const quiet = reportsShownRef.current;
    if (!quiet) { setReportsLoading(true); setReportsErr(null); }
    const path = `/api/reports?projectId=${encodeURIComponent(id)}`;
    (async () => {
      try {
        let r = await apiGet(path);
        // An empty list may be the worker's old copy: ask the network once
        // before saying there are none.
        if (!(Array.isArray(r) && r.length) && !cancelled) r = await apiGet(path, { fresh: true });
        if (cancelled) return;
        setReports(Array.isArray(r) ? r : []);
        setReportsErr(null);
        reportsShownRef.current = true;
      } catch (e) {
        if (cancelled) return;
        if (!quiet) setReportsErr(e?.message || String(e));
      } finally {
        if (!cancelled) setReportsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, reportsTick]);
  const retryReports = () => { reportsShownRef.current = false; setReportsTick(t => t + 1); };

  const currentReport = reportId
    ? reports.find(r => String(r.id ?? r.Id) === String(reportId))
    : null;

  // v0.23.0 — takes name + description from the modal. The button now
  // opens the modal; modal submit calls this. Same POST/set/navigate
  // chain as before, just with real user input replacing 'New report'.
  async function handleNewReport({ name, description, seedMembers }) {
    if (creatingReport) return;
    setCreatingReport(true);
    try {
      const created = await apiSend('POST', '/api/reports', {
        projectId: Number(id),
        name,
        description: description || '',
        settingsJson: { explicitDefectIds: [] },
        // v0.45.10 — Forward the seedMembers (owner + optional project
        // team, depending on the modal's tickbox) so the backend clones
        // them into ReportTeamMembers.
        seedMembers,
      });
      const newId = created.id ?? created.Id;
      setReports(prev => [
        { id: newId, name, description: description || '', settingsJson: { explicitDefectIds: [] }, CreatedAt: new Date().toISOString() },
        ...prev,
      ]);
      // v0.45.23 — Brett: "i asked for this page to be hidden."
      // After creating a new report, skip the report-scoped tile
      // page on ProjectDetail and land straight in the defect
      // register (Drop a Pin) where the actual work happens.
      navigate(`/projects/${id}/register?reportId=${encodeURIComponent(String(newId))}`);
      setNamingReport(false);
    } catch (e) {
      setError('Could not create report: ' + (e.message || String(e)));
      throw e; // let the modal show the error in-line
    } finally {
      setCreatingReport(false);
    }
  }

  async function save() {
    setSaving(true);
    try {
      const p = await apiSend('PATCH', `/api/projects/${id}`, {
        name: draft.name,
        address: draft.address || null,
        // v0.37.0 — customer name on Project (drives report header).
        customerName: draft.customerName?.trim() || null,
      });
      setProject(p);
      setEditing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function archive() {
    if (!confirm('Archive this location? It will be hidden from the default list but kept in the database.')) return;
    setSaving(true);
    try {
      await apiSend('DELETE', `/api/projects/${id}`);
      navigate('/projects');
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  if (loading) return <div style={{ ...card, padding: 40, textAlign: 'center', color: theme.textLight }}>Loading…</div>;
  if (error) return (
    <div>
      <button onClick={() => navigate('/projects')} style={{ ...btnSecondary, marginBottom: 12 }}>← Back to Locations</button>
      <div style={{ ...card, color: theme.danger }}>{error}</div>
    </div>
  );
  if (!project) return null;

  return (
    <div>
      {/* v0.24.0 — Compact single-row header per the clean-compact-layouts
          memory rule. Breadcrumb-back link, title + meta inline, ALL
          actions (Edit / Archive / Members / Close report) in ONE row
          on the right. When a report is active, the title becomes the
          report name with project name as the subtitle — the old
          standalone "Working on report" banner is gone (its info is
          already in the title). */}
      <div style={{
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderLeft: reportId ? `4px solid ${theme.brand}` : `1px solid ${theme.border}`,
        borderRadius: 10,
        padding: '10px 14px',
        marginBottom: 12,
      }}>
        {editing ? (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <button onClick={() => navigate('/projects')} style={crumbLink}>← Locations</button>
              <span style={{ color: theme.textLight, fontSize: 12 }}>/ editing location</span>
            </div>
            {/* v0.37.0 — three fields across (Name, Address, Customer) on
                wide screens; stacks naturally on narrow viewports. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 10 }}>
              <div>
                <Label>Name</Label>
                <input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} style={input} autoFocus />
              </div>
              <div>
                <Label>Address</Label>
                <input value={draft.address} onChange={e => setDraft({ ...draft, address: e.target.value })} style={input} />
              </div>
              <div>
                <Label>Customer</Label>
                <input
                  value={draft.customerName}
                  onChange={e => setDraft({ ...draft, customerName: e.target.value })}
                  style={input}
                  placeholder="e.g. McDonalds Australia"
                  maxLength={200}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => { setEditing(false); setDraft({ name: project.Name, address: project.Address || '', customerName: project.CustomerName || '' }); }} style={btnSecondary}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Breadcrumb row — inline, small. Sits above the title so
                the back-affordance is there but doesn't waste vertical space. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.textLight, marginBottom: 4 }}>
              <button onClick={() => navigate('/projects')} style={crumbLink}>← Locations</button>
              {reportId && currentReport && (<>
                <span>/</span>
                <button onClick={() => setSearchParams({})} style={crumbLink} title="Back to project overview">{project.Name}</button>
              </>)}
            </div>

            {/* Title row — title left, actions right, all on one line. */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>
                  {reportId ? (currentReport?.name || `Report #${reportId}`) : project.Name}
                </h1>
                <div style={{ fontSize: 12, color: theme.textLight, marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {reportId
                    ? <span>{project.Name}{project.Address ? ` · ${project.Address}` : ''}</span>
                    : (project.Address && <span>{project.Address}</span>)}
                  {/* v0.37.0 — Customer surfaced in the project meta strip. */}
                  {project.CustomerName && <span>· Customer: <strong style={{ color: theme.text }}>{project.CustomerName}</strong></span>}
                  <span>· {project.Status}</span>
                  <span>· Updated {fmtDate(project.UpdatedAt)}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {reportId && (
                  <button
                    onClick={() => setSearchParams({})}
                    style={{ ...btnSecondary, fontSize: 12, padding: '5px 10px' }}
                    title="Back to project overview"
                  >✕ Close report</button>
                )}
                <button onClick={() => setEditing(true)} style={{ ...btnSecondary, fontSize: 12, padding: '5px 10px' }}>Edit</button>
                {project.Status !== 'archived' && (
                  <button onClick={archive} disabled={saving} style={{ ...btnSecondary, color: theme.danger, fontSize: 12, padding: '5px 10px' }}>Archive</button>
                )}
                {/* v0.27.0 — Members button removed. Report distribution
                    is managed on the panel below; share-link management
                    is reachable via Manage saved reports → if needed. */}
              </div>
            </div>
          </>
        )}
      </div>

      {/* v0.339.0 — no team on a location (Brett: "Location shows nothing. Do it
          properly."). Each report holds its own team, from its register. */}

      {/* v0.22.1 — Reports list shown on PROJECT-HOME (no ?reportId).
          v0.23.0 — Hidden entirely when a report is selected. Brett:
          "If im in a report, i dont want to see another report. Thats
          why we listed all the reports on the project register." The
          active-report banner below is enough of a breadcrumb; the
          full list belongs back on the project register. */}
      {!reportId && (() => {
        // v0.45.13 — Partition reports by settingsJson.archived. The
        // main list shows open reports; archived rows render below in a
        // muted style, only when "Show archived" is toggled on, with an
        // Unarchive button. No schema change — soft state in JSON.
        const parseSettings = (r) => {
          try {
            return (typeof r.settingsJson === 'string')
              ? JSON.parse(r.settingsJson || '{}')
              : (r.settingsJson || {});
          } catch { return {}; }
        };
        const openReports     = reports.filter(r => !parseSettings(r)?.archived);
        const archivedReports = reports.filter(r =>  parseSettings(r)?.archived);

        const unarchive = async (r) => {
          const rid = String(r.id ?? r.Id);
          setArchiveBusyId(rid);
          try {
            const next = { ...parseSettings(r), archived: false };
            await apiSend('PATCH', `/api/reports/${rid}`, { settingsJson: next });
            // Reflect locally without a refetch.
            setReports(prev => prev.map(x =>
              String(x.id ?? x.Id) === rid ? { ...x, settingsJson: next } : x
            ));
          } catch (e) {
            alertFailure('Could not unarchive: ' + (e.message || e), e);
          } finally {
            setArchiveBusyId(null);
          }
        };

        return (
        <div style={{ ...card, marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: openReports.length > 0 ? 8 : 0, gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.text }}>
              Reports{openReports.length > 0 && (<span style={{ color: theme.textLight, fontWeight: 500, fontSize: 12, marginLeft: 8 }}>· {openReports.length}</span>)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* v0.45.13 — Show-archived toggle. Hidden when there are
                  no archived reports so it doesn't clutter the empty
                  state. Persisted to localStorage so the preference
                  survives reloads / tab switches. */}
              {archivedReports.length > 0 && (
                <label style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 12, color: theme.textLight, cursor: 'pointer',
                  userSelect: 'none',
                }}>
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setShowArchived(v);
                      try { localStorage.setItem('droppin.showArchivedReports', v ? '1' : '0'); } catch (_) {}
                    }}
                    style={{ margin: 0 }}
                  />
                  Show archived ({archivedReports.length})
                </label>
              )}
              <button onClick={() => setNamingReport(true)} disabled={creatingReport} style={{ ...btnPrimary, opacity: creatingReport ? 0.6 : 1 }}>
                + New report
              </button>
            </div>
          </div>
          {reportsLoading ? (
            <div style={{ fontSize: 12, color: theme.textLight, padding: '10px 0' }}>Loading reports…</div>
          ) : reportsErr ? (
            <div role="alert" data-state="reports-failed" style={{ fontSize: 13, color: theme.text, padding: '10px 0', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span>
                <strong style={{ color: theme.danger }}>Could not load this location's reports.</strong>{' '}
                <span style={{ color: theme.textLight }}>{/offline|503|Failed to fetch|NetworkError/i.test(reportsErr) ? 'No connection to DropPin right now.' : 'DropPin did not answer properly.'} This is not the same as having none.</span>
              </span>
              <button onClick={retryReports} style={{ ...btnSecondary, fontSize: 12, padding: '5px 10px' }}>Try again</button>
            </div>
          ) : openReports.length === 0 && !(showArchived && archivedReports.length > 0) ? (
            <div style={{ fontSize: 13, color: theme.textLight, padding: '10px 0' }}>
              No reports yet. Click <strong>+ New report</strong> to add one — each report owns its own plans, pins, and members.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {openReports.map(r => {
                const rid = String(r.id ?? r.Id);
                return (
                  <button
                    key={rid}
                    onClick={() => navigate(`/projects/${id}/register?reportId=${encodeURIComponent(rid)}`)}
                    style={{
                      textAlign: 'left', padding: '10px 12px', borderRadius: 8,
                      background: theme.surface,
                      border: `1px solid ${theme.border}`,
                      cursor: 'pointer', fontFamily: 'inherit',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                    }}
                    title="Open this report"
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.name || `Report #${rid}`}
                      </div>
                      {r.description && (
                        <div style={{ fontSize: 11, color: theme.textLight, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.description}</div>
                      )}
                    </div>
                  </button>
                );
              })}

              {/* v0.45.13 — Archived rows. Muted styling so they don't
                  compete visually with open reports; each row carries
                  an Unarchive button that flips settingsJson.archived
                  back to false. The row itself is still clickable in
                  case the user wants to read the archived report. */}
              {showArchived && archivedReports.length > 0 && (
                <div style={{ marginTop: 6, paddingTop: 8, borderTop: `1px dashed ${theme.border}` }}>
                  <div style={{ fontSize: 11, color: theme.textLight, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Archived · {archivedReports.length}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {archivedReports.map(r => {
                      const rid = String(r.id ?? r.Id);
                      const busy = archiveBusyId === rid;
                      return (
                        <div
                          key={rid}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            gap: 10, padding: '8px 12px', borderRadius: 8,
                            background: theme.surface, opacity: 0.65,
                            border: `1px dashed ${theme.border}`,
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => navigate(`/projects/${id}/register?reportId=${encodeURIComponent(rid)}`)}
                            style={{
                              textAlign: 'left', background: 'transparent', border: 'none',
                              padding: 0, cursor: 'pointer', fontFamily: 'inherit',
                              display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1,
                              color: theme.text,
                            }}
                            title="Open archived report"
                          >
                            <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {r.name || `Report #${rid}`} <span style={{ fontWeight: 400, color: theme.textLight }}>· archived</span>
                            </div>
                            {r.description && (
                              <div style={{ fontSize: 11, color: theme.textLight, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.description}</div>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => unarchive(r)}
                            disabled={busy}
                            style={{ ...btnSecondary, fontSize: 12, padding: '4px 10px', opacity: busy ? 0.6 : 1 }}
                            title="Restore this report to the open list"
                          >
                            {busy ? '…' : 'Unarchive'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        );
      })()}

      {/* v0.23.0 — name-the-report modal. Opened from the +New report
          button above (project-home only). On submit it POSTs the
          report and the URL updates to ?reportId=N, flipping the
          page into report-home mode automatically. */}
      {namingReport && (
        <NewReportModal
          projectName={project?.Name || project?.name}
          projectId={id}
          onCancel={() => setNamingReport(false)}
          onCreate={handleNewReport}
        />
      )}

      {/* v0.38.1 — Free-tier upgrade nudge for drawings-cap hits. */}
      <UpgradePrompt
        open={!!upgradeReason}
        reason={upgradeReason}
        onClose={() => setUpgradeReason(null)}
      />

      {/* v0.24.0 — Standalone "Working on report" banner removed.
          The header above now carries report-mode state (title is the
          report name, Close report sits in the action row) so this
          extra banner was 50px of pure noise. */}

      {/* v0.22.1 — Tiles + sendHint + manage-saved-reports + TeamPanel
          all wrapped in a Fragment that only renders when a report is
          selected. Without a selected report there's nothing for the
          tiles to act on (no plan to drop into, no pin to drop on the
          plan, no report to send). React.Fragment wrapping is
          deliberate after the v0.22.1 first-attempt JSX nesting
          disaster — fragment lets multiple sibling top-level blocks
          live inside one conditional. */}
      {reportId && (<>
      {/* v0.3.0 placeholders — Drawings + Defects coming next.
          v0.4.0 — Plan tile is now active and routes to PlanScreen. */}
      <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        {/* v0.13.1 / v0.29.0 — Drop a Plan tile. Multi-file now: drop
            N PDFs/images and the rename modal walks through them one
            by one. Drag-counter pattern prevents the hover flicker
            when the cursor passes over child elements. Click still
            opens the file picker (now also multiple). */}
        <div
          onClick={() => planFileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
          onDragEnter={(e) => {
            e.preventDefault();
            dragDepthRef.current += 1;
            if (dragDepthRef.current === 1) setDropHover(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
            if (dragDepthRef.current === 0) setDropHover(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            dragDepthRef.current = 0;
            setDropHover(false);
            const files = [...(e.dataTransfer?.files || [])];
            if (files.length === 0) return;
            acceptPlanFiles(files);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') planFileInputRef.current?.click(); }}
          style={{
            ...card,
            textAlign: 'center', padding: '32px 16px',
            cursor: 'pointer', fontFamily: 'inherit',
            border: `2px dashed ${theme.brand}`,
            background: dropHover ? theme.bg : 'transparent',
            transition: 'background-color 0.12s, box-shadow 0.12s',
            boxShadow: dropHover ? `inset 0 0 0 2px ${theme.brand}` : 'none',
            minHeight: 130,
          }}
          title="Drop one or more PDFs / images, or click to pick"
        >
          <BlueprintIcon size={48} />
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6, color: theme.text }}>Drop a Plan</div>
          <div style={{ fontSize: 11, color: theme.textLight, marginTop: 4 }}>
            {dropHover ? 'Release to upload (drop multiple to queue them)'
              : drawingsCount == null ? 'Drag PDFs or images here, or click to pick. Multiple OK.'
              : drawingsCount === 0 ? 'No plans yet — drag PDFs / images here, or click to pick.'
              : `${drawingsCount} plan${drawingsCount === 1 ? '' : 's'} uploaded · drop more to add (multi-select OK)`}
          </div>
          <input
            ref={planFileInputRef}
            type="file"
            accept=".pdf,application/pdf,image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const fs = Array.from(e.target.files || []);
              if (fs.length) acceptPlanFiles(fs);
              e.target.value = '';
            }}
          />
        </div>
        <button
          onClick={() => navigate(`/projects/${id}/register${reportId ? `?reportId=${encodeURIComponent(reportId)}` : ''}`)}
          style={{
            ...card,
            textAlign: 'center', padding: '24px 16px',
            cursor: 'pointer', fontFamily: 'inherit',
            border: `1px solid ${theme.brand}`,
          }}
          title="Browse, filter and bulk-update every defect on this report"
        >
          {/* v0.10.1 - Drop a Pin tile gets the pin glyph it always
              should have had (was a clipboard which read as "list"). */}
          <MapPin size={32} color={theme.brand} strokeWidth={2} />
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6, color: theme.text }}>Drop a Pin</div>
          {/* v0.20.2 — Subtext rewritten. Brett: "Change sub text to
              Defect Register, List of pins and the defect." CSV export
              was dropped from the v0.20.0 register toolbar anyway. */}
          <div style={{ fontSize: 11, color: theme.textLight, marginTop: 4 }}>Defect Register · list of pins and the defect.</div>
        </button>

        {/* v0.201.0 — SCAN FLOOR, beside Drop a Pin. Brett: "I see it now on
            the location page. It shouldnt be there it should be within the
            report next to drop a pin." Dropping pins and scanning the floor
            are the same job — both are things you do walking the store with
            the plan open — so it belongs in this row, next to the pin tile,
            and inside the reportId gate with the rest of them.
            Still a plain <a> to a static page: no state, no API, no shared
            component, no route. */}
        {floorScan && (
        <a
          href="/kitchen-floor-survey.html"
          style={{
            ...card,
            display: 'block', textDecoration: 'none',
            textAlign: 'center', padding: '24px 16px',
            cursor: 'pointer', fontFamily: 'inherit',
            border: `1px solid ${theme.brand}`,
          }}
          title="Scan the floor: mark each strip you have filmed on the store plan as you walk it"
        >
          <div style={{ fontSize: 32, lineHeight: 1 }} aria-hidden="true">🧱</div>
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6, color: theme.text }}>Scan Floor</div>
          <div style={{ fontSize: 11, color: theme.textLight, marginTop: 4 }}>Mark the strips you&rsquo;ve filmed on the plan.</div>
        </a>
        )}
        {/* v0.18.0 - Smart-routed Send-a-Report tile. Click is gated on
            having defects to put in the report (otherwise toast hints
            instead of navigating). When defects exist and no saved
            reports do, POSTs a default report and jumps straight to it
            — skips the intermediate /reports list. See handleSendReport
            comment above for the full state machine. */}
        <button
          onClick={handleSendReport}
          disabled={sendBusy}
          style={{
            ...card,
            textAlign: 'center', padding: '24px 16px',
            cursor: sendBusy ? 'wait' : 'pointer', fontFamily: 'inherit',
            border: `1px solid ${theme.brand}`,
            background: theme.bg,
            opacity: sendBusy ? 0.6 : 1,
          }}
          title="Compile the project's defects into a shareable report"
        >
          <FileText size={32} color={theme.brand} strokeWidth={2} />
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6, color: theme.text }}>
            {sendBusy ? 'Checking…' : 'Send a Report'}
          </div>
          {/* v0.20.2 — Brett: "Change subtext to Send or export the
              report." Tighter, less recursive than the previous
              "Drop a plan, drop a pin, SEND A REPORT" tagline (which
              is already the page header). */}
          <div style={{ fontSize: 11, color: theme.textLight, marginTop: 4 }}>Send or export the report.</div>
        </button>
        {/* v0.9.4 - Shared Reports tile removed per Brett's "Drop a plan.
            Drop a pin. Send a report." three-step framing. Share links are
            now accessed INSIDE the Reports screen via the "Manage shares"
            button next to "+ New report". */}

      </div>

      {/* v0.18.0 — Inline hint surfaced from handleSendReport. Replaces
          the silent no-op when the user clicks Send-a-Report before
          any pins exist. */}
      {sendHint && (
        <div style={{
          marginTop: 10, padding: '10px 14px',
          background: theme.bg, border: '1px solid ' + theme.warn,
          borderRadius: 8, fontSize: 12, color: theme.warn,
          display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start',
        }}>
          <span>{sendHint}</span>
          <button
            onClick={() => setSendHint(null)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: theme.warn, fontSize: 14, padding: 0 }}
            aria-label="Dismiss"
          >×</button>
        </div>
      )}

      {/* v0.18.0 — Secondary access to the saved-reports manager. Most
          projects only ever have one rolling report — the smart-route
          in Send-a-Report handles that case directly. Power users
          managing multiple named reports (Stage 1 / Stage 2 / Final)
          land here. */}
      <div style={{ marginTop: 10, textAlign: 'right' }}>
        <button
          onClick={() => navigate(`/projects/${id}/reports`)}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: theme.textLight, fontSize: 12, padding: '4px 6px',
            textDecoration: 'underline',
          }}
          title="Open the saved-reports manager (rename, delete, share, switch between multiple reports)"
        >Manage saved reports →</button>
      </div>

      {/* v0.22.1 — Members panel is now REPORT-scoped. Brett:
          "Members/plans and pins change for each report. Have a
          discrete button to import members from report to report."
          TeamPanel is mounted with kind="report" inside the same
          Fragment as the tiles so it inherits the same {reportId &&}
          guard. The v0.10.0 ProjectTeamMembers layer is no longer
          surfaced here — it stays in the DB as a starter-roster
          source if we ever wire a default-team import. */}
      <TeamPanel
        kind="report"
        parentId={parseInt(reportId, 10)}
        allReports={reports}
        activeReportId={parseInt(reportId, 10)}
      />
      </>)}

      {/* v0.13.1 - Drop-error toast (small banner). */}
      {planError && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 90,
          background: theme.bg, border: '1px solid ' + theme.danger,
          color: theme.danger, padding: '12px 14px', borderRadius: 8,
          fontSize: 12, maxWidth: 340,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
            <span>{planError}</span>
            <button
              onClick={() => setPlanError(null)}
              style={{ background: 'transparent', border: 'none', color: theme.danger, cursor: 'pointer', fontSize: 14, padding: 0 }}
            >×</button>
          </div>
        </div>
      )}

      {/* v0.13.1 / v0.29.0 — Plan rename modal. Walks the queue:
          shows "Plan X of N" when more than one file is pending,
          plus a Skip option to drop the current file without
          uploading it. Cancel kills the entire queue. */}
      {planQueue.length > 0 && (
        <PlanRenameModal
          file={planQueue[0].file}
          defaultName={planQueue[0].defaultName}
          uploading={uploadingPlan}
          queueIndex={0}
          queueTotal={planQueue.length}
          onCancel={cancelPlanQueue}
          onSkip={planQueue.length > 1 ? skipCurrentPlan : null}
          onConfirm={(nickname) => uploadCurrentPlan(nickname)}
        />
      )}
    </div>
  );
}

// v0.13.1 - Inline blueprint icon. Cyan grid + room outlines + a small
// north arrow - reads as a working drawing at 44px. Flat colours, no
// filters, no gradients (matches the v0.4.8/v0.4.12 InSpecPDF rules
// for SVG safety).
function BlueprintIcon({ size = 44 }) {
  return (
    <svg viewBox="0 0 44 44" width={size} height={size} aria-hidden="true" style={{ display: 'inline-block' }}>
      {/* paper */}
      <rect x="3" y="4" width="38" height="36" rx="2" fill="#0e3a6b" stroke="#0a2a52" strokeWidth="0.6" />
      {/* grid lines */}
      <g stroke="#5fa3d6" strokeWidth="0.35" opacity="0.55">
        <line x1="3"  y1="12" x2="41" y2="12" />
        <line x1="3"  y1="20" x2="41" y2="20" />
        <line x1="3"  y1="28" x2="41" y2="28" />
        <line x1="3"  y1="36" x2="41" y2="36" />
        <line x1="11" y1="4"  x2="11" y2="40" />
        <line x1="19" y1="4"  x2="19" y2="40" />
        <line x1="27" y1="4"  x2="27" y2="40" />
        <line x1="35" y1="4"  x2="35" y2="40" />
      </g>
      {/* room outline */}
      <g stroke="#cfe4f5" fill="none" strokeWidth="1.2">
        <path d="M 8 11 L 8 34 L 36 34 L 36 22 L 26 22 L 26 11 Z" />
        <line x1="8"  y1="24" x2="22" y2="24" />
        <line x1="22" y1="24" x2="22" y2="34" />
      </g>
      {/* door swing */}
      <path d="M 16 34 A 4 4 0 0 1 16 30" stroke="#9bc7e8" strokeWidth="0.6" fill="none" strokeDasharray="1 1" />
      {/* north arrow */}
      <g transform="translate(36 8)">
        <circle r="3" fill="none" stroke="#cfe4f5" strokeWidth="0.5" />
        <path d="M 0 -2 L 1.2 2 L 0 1 L -1.2 2 Z" fill="#cfe4f5" />
      </g>
    </svg>
  );
}

// v0.13.1 — PlanRenameModal moved to ../components/PlanRenameModal.jsx
// in v0.45.25 so PlanScreen's queued multi-file upload flow can share
// the same rename UX. Import is added at the top of this file.

function Label({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 600, color: theme.textLight, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 }}>{children}</div>;
}

// v0.24.0 — Compact breadcrumb-style link used in the new tight header.
const crumbLink = {
  background: 'transparent', border: 'none', padding: 0, margin: 0,
  color: theme.textLight, fontSize: 12, cursor: 'pointer',
  fontFamily: 'inherit', textDecoration: 'none',
};

function PlaceholderTile({ icon, title, subtitle }) {
  return (
    <div style={{ ...card, opacity: 0.55, textAlign: 'center', padding: '24px 16px' }}>
      <div style={{ fontSize: 32 }}>{icon}</div>
      <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6 }}>{title}</div>
      <div style={{ fontSize: 11, color: theme.textLight, marginTop: 4 }}>{subtitle}</div>
    </div>
  );
}
